'use client';

import { getProduct, products } from '@/lib/products';
import { BrowserShot, PhoneShot } from './shots';
import { Reveal } from './Reveal';
import { ProductHero } from './hero';
import {
  AccentGlow,
  DemoBand,
  FeatureRows,
  NextProducts,
  ProductMarquee,
  SectionHead,
  StatGrid,
} from './blocks';

const product = getProduct('dilamenu')!;
const A = product.accent;
const IMG = '/images/products/dilamenu';

const LIVE_MENU = 'https://koshky-khanadan.dila.menu';

export function DilaMenuPage() {
  return (
    <div className="self-styled relative text-white">
      {/* ── Hero: phone fan over food ────────────────────────────────── */}
      <ProductHero
        name="dila.menu"
        accent={A}
        title="digital menus and restaurant operations."
        lede="Trilingual QR menus, ordering that reaches the kitchen, and a POS designed for Iraqi hospitality teams."
        secondary={{ label: 'Open a live menu', href: LIVE_MENU, external: true }}
        liveHref={product.liveUrl}
        liveLabel={`${product.liveLabel} · ${product.domain}`}
      >
        <AccentGlow accent={A} className="left-1/3 top-1/4 h-80 w-80" opacity={0.2} />
        <div className="relative mx-auto h-[520px] max-w-[540px] sm:h-[580px]">
          <div
            className="absolute inset-x-10 top-4 bottom-4 -rotate-2 overflow-hidden rounded-2xl border border-white/10 bg-cover bg-center opacity-45"
            style={{ backgroundImage: `url(${IMG}/food-koshky.webp)` }}
            aria-hidden="true"
          />
          <div className="absolute left-0 top-16 hidden w-[34%] -rotate-[8deg] sm:block">
            <PhoneShot
              src={`${IMG}/welcome.webp`}
              alt="dila.menu welcome screen offering English, Arabic and Kurdish."
              sizes="18vw"
            />
          </div>
          <div className="absolute left-1/2 top-0 z-10 w-[48%] -translate-x-1/2 sm:w-[42%]">
            <PhoneShot src={product.image} alt={product.imageAlt} priority sizes="(max-width: 640px) 48vw, 22vw" />
          </div>
          <div className="absolute right-0 top-20 hidden w-[32%] rotate-[8deg] sm:block">
            <PhoneShot
              src={`${IMG}/item-modal.webp`}
              alt="dila.menu item detail showing weight, calories, origin and ingredients."
              sizes="17vw"
            />
          </div>
        </div>
      </ProductHero>

      {/* ── Stats strip ──────────────────────────────────────────────── */}
      <section aria-label="dila.menu in numbers" className="border-t border-white/10 px-6 py-20 md:px-12 md:py-24 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <StatGrid
              accent={A}
              stats={[
                { value: '38', label: 'Venues live' },
                { value: '11', label: 'Menu themes' },
                { value: '~25kb', label: 'Menu page weight' },
                { value: '<2s', label: 'First load' },
              ]}
            />
          </Reveal>
        </div>
      </section>

      {/* ── Themes (centered stack + the one marquee) ────────────────── */}
      <section aria-label="Themes" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              accent={A}
              title="eleven themes. one qr code."
              lede="From white-tablecloth editorial to Kurdish kilim craft, each theme is a full architecture with its own typography, rhythm and motion. Three layouts, three densities, contrast enforced."
              className="max-w-4xl"
            />
          </Reveal>
          <Reveal delay={120} className="relative mt-14">
            <AccentGlow accent={A} className="right-1/4 -top-8 h-64 w-64" opacity={0.16} />
            <BrowserShot
              src={`${IMG}/themes.webp`}
              alt="The dila.menu theme gallery showing eight distinct menu architectures."
              url="dila.menu/themes"
              accent={A}
              aspect="aspect-[16/9]"
              sizes="(max-width: 1024px) 100vw, 85vw"
            />
          </Reveal>
        </div>
        <ProductMarquee
          accent={A}
          className="mt-16"
          duration={44}
          items={[
            'Atelier',
            'Chiaroscuro',
            'Kilim Modern',
            'Brutalist Grotesque',
            'Solar Flare',
            'Mono-Chef',
            'Modern Bistro',
            'Steakhouse',
            'Hookah Lounge',
            'Bakery',
            'Traditional Kurdish',
          ]}
        />
      </section>

      {/* ── Ordering (split: copy left, shot right) ──────────────────── */}
      <section aria-label="Ordering" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <SectionHead
              accent={A}
              title="ordering without the middleman's cut"
              lede="Guests order from the table or from home. Dine-in orders land straight in the POS; pickup and delivery hand off to your WhatsApp. No marketplace commission, payments settle at your till."
            />
            <FeatureRows
              accent={A}
              className="mt-10"
              rows={[
                {
                  title: 'Reservations included',
                  body: 'Three-step booking flows into the POS, where staff confirm, assign a table, or mark a no-show.',
                },
                {
                  title: 'Call-waiter and feedback',
                  body: 'The QR on the table is also the waiter button and the five-star review box.',
                },
              ]}
            />
          </Reveal>
          <Reveal className="relative lg:col-span-7" delay={120}>
            <AccentGlow accent={A} className="-right-8 bottom-0 h-64 w-64" opacity={0.16} />
            <BrowserShot
              src={`${IMG}/pos-ku.webp`}
              alt="dila RMS floor in Kurdish with live table cards, running bills in IQD and the cashier rail, receiving dine-in orders from the QR menu."
              url="dila RMS · incoming orders"
              accent={A}
              aspect="aspect-[4/3]"
              sizes="(max-width: 1024px) 100vw, 55vw"
            />
          </Reveal>
        </div>
      </section>

      {/* ── LivePulse (split: shot left, copy right) ─────────────────── */}
      <section aria-label="Live analytics" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12">
          <Reveal className="relative order-2 lg:order-1 lg:col-span-6" delay={120}>
            <AccentGlow accent={A} className="-left-8 top-10 h-64 w-64" opacity={0.16} />
            <div className="mx-auto max-w-[380px]">
              <PhoneShot
                src={`${IMG}/livepulse.webp`}
                alt="dila.menu LivePulse admin in Kurdish showing a live revenue goal ring, top items and real-time activity feed."
                sizes="(max-width: 1024px) 80vw, 30vw"
              />
            </div>
          </Reveal>
          <Reveal className="order-1 lg:order-2 lg:col-span-6">
            <SectionHead
              accent={A}
              title="know your night while it's happening"
              lede="LivePulse rings your revenue goal as orders land, ranks tonight's top dishes and streams the activity feed. The whole admin runs on the phone in your pocket, in Kurdish, Arabic or English."
            />
            <FeatureRows
              accent={A}
              className="mt-10"
              rows={[
                {
                  title: 'Edit the menu from the floor',
                  body: 'Mark sold-out dishes as unavailable, update photos and reorder categories. The printed QR code stays the same while the menu changes.',
                },
                {
                  title: 'Print-ready QR designer',
                  body: 'Menu, order, table and Wi-Fi codes with captions and backgrounds, exported as bulk PDFs for the print shop.',
                },
              ]}
            />
          </Reveal>
        </div>
      </section>

      {/* ── Multi-vertical bento ─────────────────────────────────────── */}
      <section aria-label="Beyond restaurants" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              accent={A}
              title="More than restaurant menus"
              lede="The same system can support barbershops, laundries and motels with wording and services tailored to each type of business."
            />
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            <Reveal>
              <BrowserShot
                src={`${IMG}/barber.webp`}
                alt="A barbershop running on dila.menu with a noir theme and priced service cards in IQD."
                url="dila.menu"
                accent={A}
                aspect="aspect-[4/5]"
                sizes="(max-width: 768px) 100vw, 45vw"
              />
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
                Swan Barber Shop · noir theme
              </p>
            </Reveal>
            <Reveal delay={120} className="md:pt-16">
              <BrowserShot
                src={`${IMG}/order.webp`}
                alt="A laundry running on dila.menu taking online orders with a garment-by-service price matrix, wash and iron priced separately in IQD."
                url="dila.menu"
                accent={A}
                aspect="aspect-[4/5]"
                sizes="(max-width: 768px) 100vw, 45vw"
              />
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
                Swan Laundry · wash and iron matrix
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Food band (editorial moment) ─────────────────────────────── */}
      <section aria-label="Craft" className="relative overflow-hidden border-t border-white/10">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMG}/food-swan.webp)` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-black/70" aria-hidden="true" />
        <div className="relative px-6 py-32 md:px-12 md:py-44 lg:px-24">
          <Reveal className="mx-auto max-w-[1400px]">
            <p className="max-w-3xl font-six-caps text-6xl lowercase leading-[0.92] text-white sm:text-7xl md:text-8xl">
              a menu should feel like the space it lives in
            </p>
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: A }}>
              Warm · clear · unmistakably yours
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── dila RMS band ────────────────────────────────────────────── */}
      <section aria-label="dila RMS" className="px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px] rounded-3xl border border-white/10 bg-white/[0.03] p-7 md:p-12 lg:p-16">
          <Reveal>
            <span className="eyebrow" style={{ color: A }}>
              dila RMS · the companion POS
            </span>
            <h2 className="mt-6 max-w-3xl font-six-caps text-6xl lowercase leading-[0.9] text-white sm:text-7xl md:text-8xl">
              then the kitchen takes over
            </h2>
            <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-white/65 md:text-lg">
              dila.menu feeds the floor; dila RMS runs it. The restaurant
              management system the same venues close their nights on, for
              Windows, macOS, Android and web.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 lg:grid-cols-12">
            <Reveal className="lg:col-span-7" delay={100}>
              <BrowserShot
                src={`${IMG}/pos-tables.webp`}
                alt="dila RMS table overview with open and seated tables, running bills in IQD and live revenue chips."
                url="dila RMS · tables"
                accent={A}
                aspect="aspect-[16/10]"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </Reveal>
            <Reveal className="lg:col-span-5 lg:pt-14" delay={200}>
              <BrowserShot
                src={`${IMG}/pos-platforms.webp`}
                alt="dila RMS delivery platform picker with Talabat, Lezzoo, Toters, phone order and walk-in."
                url="dila RMS · delivery"
                accent={A}
                aspect="aspect-[4/3]"
                sizes="(max-width: 1024px) 100vw, 38vw"
              />
            </Reveal>
          </div>
          <Reveal delay={150}>
            <FeatureRows
              accent={A}
              className="mt-14"
              rows={[
                {
                  title: 'Tables, courses and running bills',
                  body: 'A live floor grid with per-table bills, VIP handling and a presence lock when two devices open the same table.',
                },
                {
                  title: 'Kitchen, bar and hookah screens',
                  body: 'Each station sees only its items, with new-order alarms and ready boards. Nothing prints where it should not.',
                },
                {
                  title: 'Iraqi payments built in',
                  body: 'Cash, card, FIB, Qi and ZainCash tenders, split payments, cashier sessions with variance, and on-credit customer ledgers.',
                },
                {
                  title: 'Delivery without chaos',
                  body: 'A live Talabat integration, Lezzoo and Toters boards, platform ref codes and payout reconciliation with short-pay protection.',
                },
                {
                  title: 'Kurdish-first printing, offline-first core',
                  body: 'Receipts print in Kurdish first, routed to the right printer, and the queue survives an internet outage mid-service.',
                },
              ]}
            />
          </Reveal>
        </div>
      </section>

      {/* ── Clients strip ────────────────────────────────────────────── */}
      <section aria-label="Clients" className="border-t border-white/10 px-6 py-20 md:px-12 md:py-24 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/45">
              Running tonight, somewhere in Kurdistan
            </p>
            <ul className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
              {[
                'Koshky Khanadan',
                'Arbab Cafe',
                'Swan Village',
                'Happy Time',
                'Hama Cafe',
                'Kabab Najar',
                'Sport Cafe',
                'Chilay',
              ].map((name) => (
                <li
                  key={name}
                  className="flex items-center gap-8 font-six-caps text-4xl lowercase leading-none text-white/75 sm:text-5xl"
                >
                  {name}
                  <span style={{ color: A }} aria-hidden="true" className="text-2xl">
                    ✦
                  </span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ── Demo + cross-sell ────────────────────────────────────────── */}
      <div className="px-6 md:px-12 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <DemoBand
            ghost="dila.menu"
            title="put your menu in every pocket"
            sub="QRs printed, theme picked, menu translated, staff trained. Most venues go live in days, and your first menu can mirror the one on your tables tonight."
            primary={{ label: 'Book a demo', href: '/contact' }}
            secondary={{ label: 'Open a live menu', href: LIVE_MENU, external: true }}
            accent={A}
            note="Live: dila.menu · koshky-khanadan.dila.menu · demo-restaurant.dila.menu"
          />
          <NextProducts
            className="mb-24"
            items={products
              .filter((p) => p.slug !== 'dilamenu')
              .map((p) => ({ slug: p.slug, name: p.name, tagline: p.tagline, accent: p.accent }))}
          />
        </div>
      </div>
    </div>
  );
}
